import Consts from '@/api/Consts';
import MapHelpers from '@/components/map/utils/MapHelpers';

import {
	HEATMAP_STATUS_OK,
	HEATMAP_STATUS_BREAK,
	HEATMAP_STATUS_SINGLE,
	HEATMAP_STATUS_MULTI,
	buildHeatmapColorSettings,
	SPOOFING_GNSS_CODES,
} from '@/utils/spoofingVisibilityHeatmapUtils';
import type { HeatmapTooltipRow, HeatmapChartPointItem } from '@/utils/spoofingVisibilityHeatmapUtils';


export type GssDisplayMode = 'sum' | 'median';


export type GssItem = {
	current_datetime: string;
	mountpoint: string;
	gnss: string;
	elevation: number;
	expected_sec: number;
	usable_sec: number;
	cover_sec: number;
	median_percent: number;
};


export const gssPercent = (usableSec: number, expectedSec: number): number => {
	if (expectedSec <= 0) {
		return 0;
	}
	return Math.round((usableSec * 100) / expectedSec);
};


export const formatGssTooltipValue = (
	percent: number,
	usableSum: number,
	expectedSum: number,
	coverSec: number,
): string => {
	return String(percent) + '% ' + String(usableSum) + ' / ' + String(expectedSum) + ' сек (' + String(coverSec) + ' сек)';
};


export const formatGssMedianTooltipValue = (
	percent: number,
	coverSec: number,
): string => {
	return String(percent) + '% (' + String(coverSec) + ' сек)';
};


type GnssBucket = {
	expectedSum: number;
	usableSum: number;
	coverSec: number;
	medianPercent: number;
};


const indexGssBucket = (items: GssItem[]): Map<string, GnssBucket> => {
	const result = new Map<string, GnssBucket>();
	for (const item of items) {
		const existing = result.get(item.gnss);
		if (existing == null) {
			result.set(item.gnss, {
				expectedSum: item.expected_sec,
				usableSum: item.usable_sec,
				coverSec: item.cover_sec,
				medianPercent: item.median_percent,
			});
		} else {
			existing.expectedSum += item.expected_sec;
			existing.usableSum += item.usable_sec;
			existing.coverSec = item.cover_sec;
		}
	}
	return result;
};


const getGnssPercent = (
	bucket: GnssBucket,
	displayMode: GssDisplayMode,
): number | null => {
	if (displayMode === 'median') {
		return bucket.medianPercent;
	}
	if (bucket.expectedSum <= 0) {
		return null;
	}
	return gssPercent(bucket.usableSum, bucket.expectedSum);
};


export const classifyGssCell = (
	items: GssItem[],
	visibilityThreshold: number,
	displayMode: GssDisplayMode = 'sum',
): { status: number | null; tooltipRows: HeatmapTooltipRow[] } => {
	if (items.length === 0) {
		return { status: null, tooltipRows: [] };
	}
	const index = indexGssBucket(items);
	const failingGnss: string[] = [];
	const tooltipRows: HeatmapTooltipRow[] = [];
	const sortedGnss = Consts.sortGnssCodes(
		Array.from(index.keys()).filter(gnss => SPOOFING_GNSS_CODES.includes(gnss)),
	);
	for (const gnss of sortedGnss) {
		const bucket = index.get(gnss);
		if (bucket == null) {
			continue;
		}
		const percent = getGnssPercent(bucket, displayMode);
		if (percent == null) {
			continue;
		}
		const gnssName = MapHelpers.getGnssDataDisplay(gnss).name;
		tooltipRows.push({
			label: gnssName,
			value: displayMode === 'median'
				? formatGssMedianTooltipValue(percent, bucket.coverSec)
				: formatGssTooltipValue(
					percent,
					bucket.usableSum,
					bucket.expectedSum,
					bucket.coverSec,
				),
		});
		if (percent < visibilityThreshold) {
			failingGnss.push(gnss);
		}
	}
	if (tooltipRows.length === 0) {
		return { status: null, tooltipRows: [] };
	}
	const coverSec = items[0]?.cover_sec;
	if (coverSec === 0) {
		return { status: HEATMAP_STATUS_BREAK, tooltipRows };
	}
	if (failingGnss.length === 0) {
		return { status: HEATMAP_STATUS_OK, tooltipRows };
	}
	if (failingGnss.length === 1) {
		return { status: HEATMAP_STATUS_SINGLE, tooltipRows };
	}
	return { status: HEATMAP_STATUS_MULTI, tooltipRows };
};


export const buildGssChartItems = (
	items: GssItem[],
	visibilityThreshold: number,
	displayMode: GssDisplayMode = 'sum',
): HeatmapChartPointItem[] => {
	const byCell = new Map<string, GssItem[]>();
	for (const item of items) {
		const key = item.mountpoint + '|' + item.current_datetime;
		const existing = byCell.get(key);
		if (existing == null) {
			byCell.set(key, [item]);
		} else {
			existing.push(item);
		}
	}
	const result: HeatmapChartPointItem[] = [];
	for (const cellItems of byCell.values()) {
		const first = cellItems[0];
		if (first == null) {
			continue;
		}
		const classification = classifyGssCell(cellItems, visibilityThreshold, displayMode);
		result.push({
			current_datetime: first.current_datetime,
			mountpoint: first.mountpoint,
			status: classification.status,
			tooltipRows: classification.tooltipRows,
		});
	}
	return result;
};


export { buildHeatmapColorSettings };
